import { sbEditable } from '@storyblok/storyblok-editable';
import classNames from 'classnames';
import type { ReactElement, Ref, RefObject } from 'react';
import { useRef, useCallback, useMemo } from 'react';
import styles from './AssetList.module.scss';
import { setupTransitionInTimeline } from './AssetList.transitions';
import type { AssetListProps, AssetListRef } from './AssetList.data';
import { useScrollTransition } from 'util/react-transition-component';
import Heading from '../../atoms/heading/Heading';
import Text from '../../atoms/text/Text';
import PrimaryButton from '../../atoms/primary-button/PrimaryButton';
import { useClientOnly } from '../../../hooks/useClientOnly';
import { useRouter } from 'next/router';

export default function AssetList({
  blok,
  ref,
  ...props
}: AssetListProps & { ref?: Ref<AssetListRef> }): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;
  const isClientOnly = useClientOnly();
  const router = useRouter();

  useScrollTransition({
    scroll: {
      trigger: elementRef,
      vars: {},
    },
    setupOptions: () => ({
      refs: {
        elementRef,
      },
      setupTransitionInTimeline,
    }),
  });

  const proxyUrl = useCallback((url: string) => {
    const domain = `https://${window.location.host}/files`;
    return url.replace('https://a.storyblok.com', domain);
  }, []);

  const fileName = useCallback((url: string) => {
    const splittedUrl = url.split('/');
    return splittedUrl[splittedUrl.length - 1];
  }, []);

  const openWindow = useCallback((url: string) => {
    window.open(url, '_blank');
  }, []);

  const copyToClipboard = (url: string) => {
    if (typeof window !== 'undefined') {
      if (!navigator.clipboard) return;
      navigator.clipboard.writeText(url);
    }
  };

  const assets = useMemo(() => {
    const hash = router.asPath.split('#')[1];
    return blok.assets.filter((item) => fileName(item.asset.filename).split('.')[0] === hash);
  }, [blok.assets, fileName, router.asPath]);

  return (
    <div
      ref={elementRef}
      className={classNames(styles.assetList, props.className)}
      {...sbEditable(blok)}
    >
      <div className={classNames(styles.header, 'grid-gutter')}>
        {blok?.title && (
          <Heading
            disableTriggerInOnScroll={false}
            as="h2"
            size="heading-01"
            className={classNames(styles.title)}
          >
            {blok?.title}
          </Heading>
        )}

        {blok?.description && (
          <Text
            disableTriggerInOnScroll={false}
            as="p"
            size="body-large"
            className={classNames(styles.description)}
          >
            {blok?.description}
          </Text>
        )}
      </div>

      {isClientOnly && (
        <ul className={classNames(styles.list)}>
          {assets.map((asset) => (
            <li key={asset._uid} className={classNames(styles.listItem, 'grid-gutter')}>
              <span className={classNames(styles.line)} />
              <div className={classNames(styles.info)}>
                <Heading
                  disableTriggerInOnScroll={false}
                  as="span"
                  size="heading-05"
                  className={classNames(styles.name)}
                  transitionInType="splitLineRotateIn"
                >
                  {asset.title}
                </Heading>
                <Text
                  disableTriggerInOnScroll={false}
                  as="span"
                  size="foot-note"
                  className={classNames(styles.file)}
                  transitionInType="splitLineRotateIn"
                >
                  {fileName(asset.asset.filename)}
                </Text>
              </div>

              <div className={classNames(styles.buttonHolder)}>
                <PrimaryButton
                  className={classNames(styles.button)}
                  label="open"
                  disableTriggerInOnScroll={false}
                  onClick={() => openWindow(proxyUrl(asset.asset.filename))}
                />
                <PrimaryButton
                  className={classNames(styles.button)}
                  label="copy link"
                  disableTriggerInOnScroll={false}
                  onClick={() => copyToClipboard(proxyUrl(asset.asset.filename))}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
